import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductsFilters } from '@interfaces/products.interface';
import { Accordion, Button, Group, Stack, Text } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';

import AccordionFilterItem from '../AccordionFilterItem';

interface ProductFiltersProps {
  availableFilters: Record<
    ProductsFilters,
    {
      hasMoreValues: boolean;
      values: { name: string; count: number }[];
    }
  >;
  selectedFilters: Record<ProductsFilters, string[]>;
  changeSelectedFilters: (
    filter: ProductsFilters,
    selectedValues: string[],
  ) => void;
  loadMoreFilterValues: (filter: ProductsFilters) => void;
  clearAllFilters: (searchQuery?: string) => void;
  setFilterSearchQuery: (
    filter: ProductsFilters,
    filterSearchQuery: string,
  ) => void;
  filtersFetchingState: Record<ProductsFilters, boolean>;
  marketCategory: string;
}

export default function ProductFilters({
  availableFilters,
  selectedFilters,
  changeSelectedFilters,
  loadMoreFilterValues,
  clearAllFilters,
  filtersFetchingState,
  setFilterSearchQuery,
  marketCategory,
}: ProductFiltersProps) {
  const { t } = useTranslation();

  // ==========================================================================
  // Memo
  // ==========================================================================
  const hasActiveFilters = useMemo(
    () =>
      Object.values(selectedFilters).some((value) =>
        Array.isArray(value) ? value.length > 0 : value !== '',
      ),
    [selectedFilters],
  );

  const getFilterTitle = (filterKey: string): string => {
    if (filterKey === 'classifications' && marketCategory === 'Cosmetico') {
      return t('filters.function');
    }
    return t(`filters.${filterKey.toLowerCase()}`);
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Stack pos="relative">
      <Group justify="space-between" align="center">
        <Text fw="bold" size="lg">
          {t('search.filters')}
        </Text>

        {hasActiveFilters && (
          <Button
            onClick={() => clearAllFilters()}
            size="xs"
            leftSection={<IconTrash size="18" />}
          >
            {t('search.removeAllFilters')}
          </Button>
        )}
      </Group>

      <Accordion
        pos="relative"
        styles={{
          control: { backgroundColor: '#ddd', border: 'none' },
          panel: { border: '1px solid #ddd' },
          content: { padding: 'var(--mantine-spacing-md)' },
        }}
        defaultValue="0"
      >
        {Object.entries(availableFilters).map(([filterKey, items]) => {
          return (
            <Fragment key={filterKey}>
              {/* // TODO: mostrare caricamento infinity loading */}
              <AccordionFilterItem
                title={getFilterTitle(filterKey)}
                filterKey={filterKey}
                selectedValues={selectedFilters[filterKey]}
                changeSelectedValues={(selectedValues) =>
                  changeSelectedFilters(filterKey, selectedValues)
                }
                loadMoreValues={() => loadMoreFilterValues(filterKey)}
                hasMoreValues={items.hasMoreValues}
                values={items.values}
                isFetching={filtersFetchingState[filterKey]}
                setFilterSearchQuery={(filterSearchQuery) =>
                  setFilterSearchQuery(filterKey, filterSearchQuery)
                }
              />
            </Fragment>
          );
        })}
      </Accordion>
    </Stack>
  );
}
